import type {
  PaymentConnection,
  Tip,
  TipPolicy,
  ExternalTipLink,
  Money,
} from '@stage/contracts';
import { newRequestId, parseError } from './client.js';

/**
 * Tipping / payments API client (M6, §22): Stripe Connect onboarding, per-gig tip-config, and tip
 * reporting. Shares the {@link ApiError}/`ErrorEnvelope` contract and bearer-token pattern of
 * {@link createAdminClient}.
 *
 * §22.3 security: a {@link PaymentConnection} exposes only SAFE fields — `providerAccountRef`,
 * `status`, `capabilities`, `currencies`, `safeMetadata`. It NEVER carries Stripe secrets or keys;
 * onboarding/dashboard links are minted server-side and returned as one-time redirect URLs.
 *
 * The client never stores tokens: it reads the current access token through the injected getter on
 * each request so silent refresh (§32.3) is transparent to callers.
 */

const BASE = '/v1';

/** Body for `POST /v1/artists/{artistId}/payment-connection/onboarding-link` (§22.3 step 1). */
export interface OnboardingLinkInput {
  /** Where Stripe returns the owner after a completed onboarding hop. */
  returnUrl: string;
  /** Where Stripe sends the owner if the hosted link expires and must be refreshed. */
  refreshUrl: string;
}

/** Result of the onboarding-link mint: a one-time Stripe-hosted URL plus the account reference. */
export interface OnboardingLinkResult {
  url: string;
  providerAccountRef: string;
}

/** Result of the dashboard-link mint: a one-time Stripe Express dashboard URL. */
export interface DashboardLinkResult {
  url: string;
}

/** Aggregated tip totals for a gig (§22.8). `currency` is null until a native tip exists. */
export interface TipSummary {
  currency: string | null;
  grossMinor: number;
  count: number;
  refundedMinor: number;
  disputeCount: number;
}

/** Result of `GET /v1/gigs/{gigId}/tips` (§22.8). */
export interface TipsResult {
  items: Tip[];
  summary: TipSummary;
}

/** Result of `GET`/`PUT` `/v1/gigs/{gigId}/tip-config` (§22.4, §22.7). */
export interface TipConfig {
  policy: TipPolicy;
  externalLinks: ExternalTipLink[];
}

/**
 * Body for `PUT /v1/gigs/{gigId}/tip-config`. Presets are integer minor units — the editor converts
 * dollars→minor at the edge (see {@link dollarsToMinor}); the wire never carries a float.
 */
export interface TipConfigInput {
  policy: {
    heading?: string;
    thankYouText?: string;
    presets: Money[];
    allowCustom: boolean;
  };
  externalLinks: ExternalTipLink[];
}

export interface PaymentsClient {
  /** SAFE payment-connection for an artist, or `null` when none has been created yet. */
  getPaymentConnection(artistId: string): Promise<PaymentConnection | null>;
  createOnboardingLink(
    artistId: string,
    input: OnboardingLinkInput,
  ): Promise<OnboardingLinkResult>;
  createDashboardLink(artistId: string): Promise<DashboardLinkResult>;
  getTips(gigId: string): Promise<TipsResult>;
  getTipConfig(gigId: string): Promise<TipConfig>;
  putTipConfig(gigId: string, input: TipConfigInput): Promise<TipConfig>;
}

/**
 * Create a payments client bound to a live access-token getter, mirroring {@link createAdminClient}.
 * The getter is read per request so silent refresh is transparent (§32.3).
 */
export function createPaymentsClient(getAccessToken: () => string | null): PaymentsClient {
  async function request<T>(path: string, init?: RequestInit): Promise<T> {
    const token = getAccessToken();
    const headers = new Headers(init?.headers);
    headers.set('accept', 'application/json');
    headers.set('x-request-id', newRequestId());
    if (token) headers.set('authorization', `Bearer ${token}`);
    if (init?.body && !headers.has('content-type')) {
      headers.set('content-type', 'application/json');
    }
    const res = await fetch(`${BASE}${path}`, { ...init, headers });
    if (!res.ok) throw await parseError(res);
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  const enc = encodeURIComponent;

  return {
    getPaymentConnection(artistId) {
      return request<PaymentConnection | null>(`/artists/${enc(artistId)}/payment-connection`);
    },
    createOnboardingLink(artistId, input) {
      return request<OnboardingLinkResult>(
        `/artists/${enc(artistId)}/payment-connection/onboarding-link`,
        { method: 'POST', body: JSON.stringify(input) },
      );
    },
    createDashboardLink(artistId) {
      return request<DashboardLinkResult>(
        `/artists/${enc(artistId)}/payment-connection/dashboard-link`,
        { method: 'POST' },
      );
    },
    getTips(gigId) {
      return request<TipsResult>(`/gigs/${enc(gigId)}/tips`);
    },
    getTipConfig(gigId) {
      return request<TipConfig>(`/gigs/${enc(gigId)}/tip-config`);
    },
    putTipConfig(gigId, input) {
      return request<TipConfig>(`/gigs/${enc(gigId)}/tip-config`, {
        method: 'PUT',
        body: JSON.stringify(input),
      });
    },
  };
}
